import React, { Component } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://www.ridenrepair.com/api';

const EXPERIENCE_BANDS = [
  { value: 'fresher', label: 'Fresher' },
  { value: '0-1', label: 'Less than 1 year' },
  { value: '1-3', label: '1 - 3 years' },
  { value: '3-5', label: '3 - 5 years' },
  { value: '5-8', label: '5 - 8 years' },
  { value: '8+', label: '8+ years' },
];

const MAX_RESUME_BYTES = 5 * 1024 * 1024;
const RESUME_EXT = /\.(pdf|doc|docx)$/i;

/**
 * Careers application form — posts multipart to POST /api/careers/apply.
 *
 * Props:
 *   jobs          - [{ slug, title, department }] to populate the role picker
 *   defaultSlug   - preselected job slug (job detail page passes its own)
 *   lockRole      - true on a job detail page: the role is fixed, shown as a chip
 *   source        - 'web_careers_page' | 'web_careers_job_page'
 *   compact       - tighter layout for the sidebar variant
 */
class CareersApplyForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      full_name: '',
      email: '',
      phone: '',
      current_city: '',
      job_slug: props.defaultSlug || '',
      experience_years: '',
      current_employer: '',
      linkedin_url: '',
      cover_letter: '',
      resumeName: '',
      resumeError: '',
      errors: {},
      submitting: false,
      submitError: '',
      reference: '',
    };
    this.resumeRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.defaultSlug !== this.props.defaultSlug && this.props.defaultSlug) {
      this.setState({ job_slug: this.props.defaultSlug });
    }
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      [name]: value,
      submitError: '',
      errors: { ...prev.errors, [name]: '' },
    }));
  };

  handleResume = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      this.setState({ resumeName: '', resumeError: '' });
      return;
    }
    if (!RESUME_EXT.test(file.name)) {
      this.setState({ resumeName: '', resumeError: 'Please upload a PDF, DOC or DOCX file.' });
      e.target.value = '';
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      this.setState({ resumeName: '', resumeError: 'That file is over 5MB. Please upload a smaller one.' });
      e.target.value = '';
      return;
    }
    this.setState({ resumeName: file.name, resumeError: '' });
  };

  validate = () => {
    const errors = {};
    const { full_name, email, phone, job_slug } = this.state;
    const { lockRole } = this.props;

    if (String(full_name).trim().length < 2) errors.full_name = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).trim())) errors.email = 'Please enter a valid email address.';
    if (!/^[6-9]\d{9}$/.test(String(phone).replace(/\D/g, '').slice(-10))) errors.phone = 'Please enter a valid 10-digit mobile number.';
    if (!lockRole && !job_slug) errors.job_slug = 'Please choose the role you are applying for.';

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    if (this.state.submitting) return;
    if (!this.validate()) return;

    const { jobs = [], defaultSlug, lockRole, source } = this.props;
    const slug = lockRole ? defaultSlug : this.state.job_slug;
    const job = jobs.find((j) => j.slug === slug);

    this.setState({ submitting: true, submitError: '' });

    try {
      const form = new FormData();
      form.append('full_name', String(this.state.full_name).trim());
      form.append('email', String(this.state.email).trim());
      form.append('phone', String(this.state.phone).replace(/\D/g, '').slice(-10));
      form.append('job_slug', slug || 'general');
      form.append('job_title', (job && job.title) || 'Open Application');
      if (job && job.department) form.append('department', job.department);
      if (this.state.current_city) form.append('current_city', String(this.state.current_city).trim());
      if (this.state.experience_years) form.append('experience_years', this.state.experience_years);
      if (this.state.current_employer) form.append('current_employer', String(this.state.current_employer).trim());
      if (this.state.linkedin_url) form.append('linkedin_url', String(this.state.linkedin_url).trim());
      if (this.state.cover_letter) form.append('cover_letter', String(this.state.cover_letter).trim());
      form.append('source', source || 'web_careers_page');
      if (typeof window !== 'undefined') {
        form.append('page_url', window.location.href);
        if (document.referrer) form.append('referrer', document.referrer);
        const qs = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign'].forEach((k) => {
          if (qs.get(k)) form.append(k, qs.get(k));
        });
      }
      const file = this.resumeRef.current && this.resumeRef.current.files && this.resumeRef.current.files[0];
      if (file) form.append('resume', file);

      const res = await axios.post(`${API_BASE}/careers/apply`, form, { timeout: 60000 });

      if (res.data && res.data.code === 1) {
        this.setState({ submitting: false, reference: res.data.reference || 'RECEIVED' });
      } else {
        this.setState({
          submitting: false,
          submitError: (res.data && res.data.message) || 'Something went wrong. Please try again.',
          errors: (res.data && res.data.errors) || {},
        });
      }
    } catch (err) {
      const data = err && err.response && err.response.data;
      this.setState({
        submitting: false,
        submitError: (data && data.message) || 'We could not submit your application. Please try again in a moment.',
        errors: (data && data.errors) || {},
      });
    }
  };

  render() {
    const { jobs = [], lockRole, defaultSlug, compact } = this.props;
    const {
      full_name, email, phone, current_city, job_slug, experience_years,
      current_employer, linkedin_url, cover_letter, resumeName, resumeError,
      errors, submitting, submitError, reference,
    } = this.state;

    if (reference) {
      const lockedJob = jobs.find((j) => j.slug === (lockRole ? defaultSlug : job_slug));
      return (
        <div className="rr-careers-form rr-careers-form--done" role="status" aria-live="polite">
          <div className="rr-careers-form__tick" aria-hidden="true">✓</div>
          <h3 className="rr-careers-form__doneTitle">Application received</h3>
          <p className="rr-careers-form__doneText">
            Thank you{lockedJob ? <> for applying for <strong>{lockedJob.title}</strong></> : ''}. Our team reviews every
            application and gets back to shortlisted candidates within 7 working days.
          </p>
          <p className="rr-careers-form__ref">
            Your reference number <span>{reference}</span>
          </p>
        </div>
      );
    }

    const lockedJob = lockRole ? jobs.find((j) => j.slug === defaultSlug) : null;

    return (
      <form
        className={`rr-careers-form${compact ? ' rr-careers-form--compact' : ''}`}
        onSubmit={this.handleSubmit}
        noValidate
      >
        {lockedJob && (
          <div className="rr-careers-form__lockedRole">
            <span className="rr-careers-form__lockedLabel">Applying for</span>
            <span className="rr-careers-form__lockedValue">{lockedJob.title}</span>
          </div>
        )}

        <div className="rr-careers-form__row">
          <div className="rr-careers-form__field">
            <label htmlFor="cf-name">Full name <span aria-hidden="true">*</span></label>
            <input
              id="cf-name" name="full_name" type="text" autoComplete="name"
              className={errors.full_name ? 'is-invalid' : ''}
              value={full_name} onChange={this.handleChange} placeholder="Your full name"
            />
            {errors.full_name && <span className="rr-careers-form__err">{errors.full_name}</span>}
          </div>
          <div className="rr-careers-form__field">
            <label htmlFor="cf-phone">Mobile number <span aria-hidden="true">*</span></label>
            <input
              id="cf-phone" name="phone" type="tel" inputMode="numeric" autoComplete="tel"
              className={errors.phone ? 'is-invalid' : ''}
              value={phone} onChange={this.handleChange} placeholder="10-digit mobile number"
            />
            {errors.phone && <span className="rr-careers-form__err">{errors.phone}</span>}
          </div>
        </div>

        <div className="rr-careers-form__row">
          <div className="rr-careers-form__field">
            <label htmlFor="cf-email">Email <span aria-hidden="true">*</span></label>
            <input
              id="cf-email" name="email" type="email" autoComplete="email"
              className={errors.email ? 'is-invalid' : ''}
              value={email} onChange={this.handleChange} placeholder="you@example.com"
            />
            {errors.email && <span className="rr-careers-form__err">{errors.email}</span>}
          </div>
          <div className="rr-careers-form__field">
            <label htmlFor="cf-city">Current city</label>
            <input
              id="cf-city" name="current_city" type="text" autoComplete="address-level2"
              value={current_city} onChange={this.handleChange} placeholder="e.g. Bengaluru"
            />
          </div>
        </div>

        <div className="rr-careers-form__row">
          {!lockRole && (
            <div className="rr-careers-form__field">
              <label htmlFor="cf-role">Role <span aria-hidden="true">*</span></label>
              <select
                id="cf-role" name="job_slug"
                className={errors.job_slug ? 'is-invalid' : ''}
                value={job_slug} onChange={this.handleChange}
              >
                <option value="">Select a role</option>
                {jobs.map((j) => (
                  <option key={j.slug} value={j.slug}>{j.title}</option>
                ))}
                <option value="general">Something else — open application</option>
              </select>
              {errors.job_slug && <span className="rr-careers-form__err">{errors.job_slug}</span>}
            </div>
          )}
          <div className="rr-careers-form__field">
            <label htmlFor="cf-exp">Experience</label>
            <select id="cf-exp" name="experience_years" value={experience_years} onChange={this.handleChange}>
              <option value="">Select experience</option>
              {EXPERIENCE_BANDS.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>
          {lockRole && (
            <div className="rr-careers-form__field">
              <label htmlFor="cf-employer">Current employer</label>
              <input
                id="cf-employer" name="current_employer" type="text"
                value={current_employer} onChange={this.handleChange} placeholder="Where you work now"
              />
            </div>
          )}
        </div>

        {!lockRole && (
          <div className="rr-careers-form__row">
            <div className="rr-careers-form__field">
              <label htmlFor="cf-employer">Current employer</label>
              <input
                id="cf-employer" name="current_employer" type="text"
                value={current_employer} onChange={this.handleChange} placeholder="Where you work now"
              />
            </div>
            <div className="rr-careers-form__field">
              <label htmlFor="cf-linkedin">LinkedIn / portfolio</label>
              <input
                id="cf-linkedin" name="linkedin_url" type="url"
                value={linkedin_url} onChange={this.handleChange} placeholder="https://"
              />
            </div>
          </div>
        )}

        <div className="rr-careers-form__field">
          <label htmlFor="cf-resume">Resume</label>
          <label className="rr-careers-form__file" htmlFor="cf-resume">
            <span className="rr-careers-form__fileBtn">Choose file</span>
            <span className="rr-careers-form__fileName">{resumeName || 'PDF, DOC or DOCX — up to 5MB'}</span>
          </label>
          <input
            id="cf-resume" ref={this.resumeRef} type="file" accept=".pdf,.doc,.docx"
            onChange={this.handleResume} className="rr-careers-form__fileInput"
          />
          {resumeError && <span className="rr-careers-form__err">{resumeError}</span>}
        </div>

        <div className="rr-careers-form__field">
          <label htmlFor="cf-cover">Anything you want us to know?</label>
          <textarea
            id="cf-cover" name="cover_letter" rows={compact ? 3 : 4}
            value={cover_letter} onChange={this.handleChange}
            placeholder="A few lines about your experience and why this role."
          />
        </div>

        {submitError && <div className="rr-careers-form__alert">{submitError}</div>}

        <button type="submit" className="rr-careers-form__submit" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit application'}
        </button>

        <p className="rr-careers-form__legal">
          By applying you agree to our <a href="/privacy-policy">privacy policy</a>. We only use your details to
          evaluate your application.
        </p>
      </form>
    );
  }
}

export default CareersApplyForm;
